/**
 * PAPPO — ContactList Component
 * Lista de contactos com avatar, nome e badge de status. Cada linha tem um
 * menu ⋮ (kebab) com "Renomear" (apelido local) e "Arquivar/Desarquivar
 * conversa". O apelido, quando existe, é mostrado NO LUGAR do nome real.
 */

import { useState, useEffect, useRef } from 'react';
import { MoreVertical, Pencil, Archive, ArchiveRestore, Ban, ShieldOff, Flag, Check, X } from 'lucide-react';
import type { Contact } from '../../types/user';
import { Avatar } from '../common/Avatar';
import styles from './ContactList.module.css';

interface ContactListProps {
  contacts: Contact[];
  /** Callback ao clicar num contacto (inicia a conversa) */
  onSelect?: (contact: Contact) => void;
  /** Define/atualiza o apelido local (alias vazio remove) */
  onRename?: (contact: Contact, alias: string) => void;
  /** Alterna arquivar/desarquivar a conversa com o contacto */
  onArchiveToggle?: (contact: Contact, archived: boolean) => void;
  /** Bloqueia o contacto (efeito 1:1 bidirecional) */
  onBlock?: (contact: Contact, targetId: number) => void;
  /** Desbloqueia o contacto */
  onUnblock?: (contact: Contact, targetId: number) => void;
  /** Denuncia o contacto à moderação (só responsáveis; alvos não-família) */
  onReport?: (contact: Contact, targetId: number) => void;
}

/** Vínculos familiares nunca oferecem "Bloquear" (mas podem ser desbloqueados) */
const FAMILY_RELATIONSHIPS = ['child', 'parent', 'co_guardian'];

const STATUS_LABELS: Record<string, string> = {
  accepted: 'Aceite',
  pending: 'Pendente',
  blocked: 'Bloqueado',
};

const RELATIONSHIP_LABELS: Record<string, string> = {
  child: 'Filho(a)',
  parent: 'Responsável',
  co_guardian: 'Co-responsável',
};

/** Nome a exibir: apelido local tem prioridade sobre o display_name real */
function displayName(contact: Contact): string {
  return (
    contact.alias?.trim() ||
    contact.contact_user?.display_name ||
    'Utilizador'
  );
}

export function ContactList({
  contacts,
  onSelect,
  onRename,
  onArchiveToggle,
  onBlock,
  onUnblock,
  onReport,
}: ContactListProps) {
  /** id do utilizador-alvo cujo menu ⋮ está aberto */
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  /** id do utilizador-alvo em renomeação inline + valor do input */
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const rootRef = useRef<HTMLUListElement>(null);

  /* Fecha o menu ao clicar fora */
  useEffect(() => {
    if (menuOpenId === null) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpenId]);

  if (contacts.length === 0) {
    return (
      <div className={styles.empty}>
        <p>Nenhum contacto encontrado</p>
      </div>
    );
  }

  const startRename = (contact: Contact, targetId: number) => {
    setMenuOpenId(null);
    setRenamingId(targetId);
    setRenameValue(contact.alias ?? '');
  };

  const commitRename = (contact: Contact) => {
    onRename?.(contact, renameValue.trim());
    setRenamingId(null);
  };

  return (
    <ul
      ref={rootRef}
      className={styles.list}
      role="list"
      aria-label="Lista de contactos"
    >
      {contacts.map((contact) => {
        const user = contact.contact_user;
        const targetId = user?.id ?? contact.id;
        const name = displayName(contact);
        const subtitle = contact.relationship
          ? RELATIONSHIP_LABELS[contact.relationship] ??
            (user?.role === 'child' ? 'Menor' : 'Responsável')
          : user?.role === 'child'
          ? 'Menor'
          : 'Responsável';
        const isRenaming = renamingId === targetId;
        const isMenuOpen = menuOpenId === targetId;
        /* Só oferece arquivar quando já há uma conversa (ou já arquivada) */
        const canArchive = contact.archived || contact.conversation_id != null;
        /* Família (filho/responsável/co-responsável) nunca oferece "Bloquear" */
        const isFamily = FAMILY_RELATIONSHIPS.includes(contact.relationship ?? '');

        return (
          <li key={targetId} className={styles.row}>
            <button
              className={styles.item}
              onClick={() => onSelect?.(contact)}
              type="button"
              disabled={isRenaming}
            >
              <Avatar
                name={name}
                src={user?.avatar_url}
                size="md"
                online={user?.is_active}
              />
              <div className={styles.info}>
                {isRenaming ? (
                  <input
                    className={styles.renameInput}
                    value={renameValue}
                    autoFocus
                    maxLength={80}
                    placeholder={user?.display_name ?? 'Apelido'}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename(contact);
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                  />
                ) : (
                  <>
                    <span className={styles.name}>{name}</span>
                    <span className={styles.role}>{subtitle}</span>
                  </>
                )}
              </div>
              {!isRenaming && (
                contact.blocked ? (
                  <span className={`${styles.status} ${styles.blocked}`}>
                    {STATUS_LABELS.blocked}
                  </span>
                ) : (
                  <span className={`${styles.status} ${styles[contact.status]}`}>
                    {STATUS_LABELS[contact.status] ?? contact.status}
                  </span>
                )
              )}
            </button>

            {isRenaming ? (
              <div className={styles.renameActions}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label="Guardar apelido"
                  onClick={() => commitRename(contact)}
                >
                  <Check size={18} />
                </button>
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label="Cancelar"
                  onClick={() => setRenamingId(null)}
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <div className={styles.menuWrap}>
                <button
                  type="button"
                  className={styles.kebab}
                  aria-label="Mais opções"
                  aria-haspopup="menu"
                  aria-expanded={isMenuOpen}
                  onClick={() =>
                    setMenuOpenId(isMenuOpen ? null : targetId)
                  }
                >
                  <MoreVertical size={18} />
                </button>

                {isMenuOpen && (
                  <div className={styles.menu} role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.menuItem}
                      onClick={() => startRename(contact, targetId)}
                    >
                      <Pencil size={15} />
                      Renomear
                    </button>

                    {canArchive && (
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.menuItem}
                        onClick={() => {
                          setMenuOpenId(null);
                          onArchiveToggle?.(contact, !contact.archived);
                        }}
                      >
                        {contact.archived ? (
                          <>
                            <ArchiveRestore size={15} />
                            Desarquivar conversa
                          </>
                        ) : (
                          <>
                            <Archive size={15} />
                            Arquivar conversa
                          </>
                        )}
                      </button>
                    )}

                    {contact.blocked ? (
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.menuItem}
                        onClick={() => {
                          setMenuOpenId(null);
                          onUnblock?.(contact, targetId);
                        }}
                      >
                        <ShieldOff size={15} />
                        Desbloquear
                      </button>
                    ) : (
                      !isFamily && (
                        <button
                          type="button"
                          role="menuitem"
                          className={styles.menuItem}
                          onClick={() => {
                            setMenuOpenId(null);
                            onBlock?.(contact, targetId);
                          }}
                        >
                          <Ban size={15} />
                          Bloquear
                        </button>
                      )
                    )}

                    {onReport && !isFamily && (
                      <button
                        type="button"
                        role="menuitem"
                        className={styles.menuItem}
                        onClick={() => {
                          setMenuOpenId(null);
                          onReport(contact, targetId);
                        }}
                      >
                        <Flag size={15} />
                        Denunciar
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
